import FetchClient from "./FetchClient";
import { getTokensFromStorage } from "./TokenService";
import { getFirstTouch } from "./FirstTouchService";
import { getLogfrogVisitorId } from "../utils/logfrog";
import { track } from "../utils/analytics";

/**
 * User-lifecycle attribution: which campaign/landing a web account came from
 * and how far it got through onboarding. Every call is fire-and-forget; the
 * server keeps first-touch / furthest-step and ignores old accounts, so these
 * are safe to repeat and must never block or throw into the UI.
 */

export const ONBOARDING_STEPS = [
  "welcome",
  "register",
  "verify",
  "deal_mode",
  "paywall",
  "zipcode",
  "stores",
  "configuration",
  "scanning",
  "completed",
] as const;
export type OnboardingStep = (typeof ONBOARDING_STEPS)[number];

const REPORTED_STEPS_KEY = "dealhawk_onboarding_steps_reported";
const REPORTED_ACQUISITION_KEY = "dealhawk_acquisition_reported";

const memoryReportedSteps = new Set<string>();
let memoryReportedAcquisition = "";

function hasAuthToken() {
  try {
    return !!getTokensFromStorage()?.accessToken;
  } catch {
    return false;
  }
}

function readReportedSteps(): Set<string> {
  const steps = new Set(memoryReportedSteps);
  try {
    const raw = sessionStorage.getItem(REPORTED_STEPS_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    if (Array.isArray(parsed)) {
      for (const step of parsed) if (typeof step === "string") steps.add(step);
    }
  } catch {
    /* Fall back to the in-memory set. */
  }
  return steps;
}

function writeReportedSteps(steps: Set<string>) {
  memoryReportedSteps.clear();
  for (const step of steps) memoryReportedSteps.add(step);
  try {
    sessionStorage.setItem(REPORTED_STEPS_KEY, JSON.stringify([...steps]));
  } catch {
    /* In-memory dedupe still applies for this page load. */
  }
}

/**
 * Report that the signed-in user reached an onboarding step, once per step per
 * browser session. Also sends the matching `onboarding_step_viewed` LogFrog event.
 */
export function reportOnboardingStep(step: OnboardingStep): void {
  try {
    if (!(ONBOARDING_STEPS as readonly string[]).includes(step)) return;
    if (!hasAuthToken()) return;
    const reported = readReportedSteps();
    if (reported.has(step)) return;
    reported.add(step);
    writeReportedSteps(reported);

    track("onboarding_step_viewed", { step });
    FetchClient.post("/lifecycle/onboarding-step", { step }).catch(() => {
      // Let a later view of this step retry after a network/server failure.
      const current = readReportedSteps();
      current.delete(step);
      writeReportedSteps(current);
    });
  } catch {
    /* Lifecycle telemetry must never interrupt onboarding. */
  }
}

/** Forget per-session step dedupe (explicit logout, so the next account reports fresh). */
export function resetLifecycleReporting(): void {
  memoryReportedSteps.clear();
  memoryReportedAcquisition = "";
  try {
    sessionStorage.removeItem(REPORTED_STEPS_KEY);
    sessionStorage.removeItem(REPORTED_ACQUISITION_KEY);
  } catch {
    /* ignore */
  }
}

export type AcquisitionPayload = {
  signupPlatform: "web";
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  referrerDomain?: string;
  landingPath?: string;
  firstSeenAt?: number;
  webVisitorId?: string;
};

export function buildAcquisitionPayload(): AcquisitionPayload {
  const payload: AcquisitionPayload = { signupPlatform: "web" };
  let touch: ReturnType<typeof getFirstTouch> = null;
  try {
    touch = getFirstTouch();
  } catch {
    touch = null;
  }
  if (touch) {
    if (touch.utm_source) payload.utmSource = touch.utm_source;
    if (touch.utm_medium) payload.utmMedium = touch.utm_medium;
    if (touch.utm_campaign) payload.utmCampaign = touch.utm_campaign;
    if (touch.utm_term) payload.utmTerm = touch.utm_term;
    if (touch.utm_content) payload.utmContent = touch.utm_content;
    if (touch.referrerDomain) payload.referrerDomain = touch.referrerDomain;
    if (touch.landingPath) payload.landingPath = touch.landingPath;
    if (touch.firstSeenAt) payload.firstSeenAt = touch.firstSeenAt;
  }
  try {
    const visitorId = getLogfrogVisitorId();
    if (visitorId) payload.webVisitorId = visitorId;
  } catch {
    /* Storage blocked: send what we have. */
  }
  return payload;
}

/**
 * Send this browser's first-touch acquisition data for the signed-in account.
 * Deduped per account per session; the server keeps first-touch only and
 * ignores accounts older than three days.
 */
function hashAccountKey(value: string): string {
  // Dedupe marker only; keeps the raw account id (an email) out of storage.
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `a${(hash >>> 0).toString(36)}`;
}

export function reportAcquisition(accountKey: string): void {
  try {
    const normalized = String(accountKey || "").trim().toLowerCase();
    if (!normalized || !hasAuthToken()) return;
    const key = hashAccountKey(normalized);
    let reported = memoryReportedAcquisition;
    try {
      reported = sessionStorage.getItem(REPORTED_ACQUISITION_KEY) || reported;
    } catch {
      /* use in-memory value */
    }
    if (reported === key) return;
    memoryReportedAcquisition = key;
    try {
      sessionStorage.setItem(REPORTED_ACQUISITION_KEY, key);
    } catch {
      /* ignore */
    }
    FetchClient.post("/lifecycle/acquisition", buildAcquisitionPayload()).catch(() => {
      memoryReportedAcquisition = "";
      try {
        sessionStorage.removeItem(REPORTED_ACQUISITION_KEY);
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* Attribution must never interrupt sign-in. */
  }
}
